import { Directive, OnInit, ViewContainerRef, Input, NgModule, OnDestroy } from "@angular/core";
// import {CommonUtils} from "./common-utils";
/**
 * 方便的定义一个渲染器视图的插槽
 */
@Directive({
    selector: '[jigsaw-renderer-host]',
})
export class JigsawRendererHost {
    constructor(public viewContainerRef: ViewContainerRef) {
    }
}

@NgModule({
    declarations: [JigsawRendererHost], exports: [JigsawRendererHost]
})
export class JigsawCommonModule {
}

export interface IDynamicInstantiatable {
    initData: any;
}

/**
 * Jigsaw所有的组件都实现这个接口
 */
export interface IJigsawComponent {
    /**
     * @internal
     */
    basicClass: string;
    width: string;
    height: string;
    maxHeight: string;
}

/**
 * 一般来说，应用无需关注此类
 */
export abstract class AbstractJigsawViewBase implements OnInit, OnDestroy {
    private _timerCache = [];

    /**
     * Angular的`OnInit`钩子是否已经执行过，是则为true，否则为false。
     * 方便子类判断当前组件的状态。
     *
     */
    protected initialized: boolean = false;

    /**
     * 延迟一会再执行一些逻辑，用法和`setTimeout`一模一样，差别在于更加安全，当组件被销毁了后，
     * `setTimeout`里已经设置的逻辑依然会被执行，这在某些特定情况下会造成意外，从而造成组件出错。
     * 使用本方法启动的异步逻辑在组件销毁之后，不再被执行。
     *

     */
    protected callLater(handler: Function);

    protected callLater(handler: Function, timeout: number);

    protected callLater(handler: Function, context: any);

    protected callLater(handler: Function, context: any, timeout: number);

    protected callLater(handler: Function, contextOrTimeout: any | number = undefined, timeout: number = 0): any {
        if (!this._timerCache) {
            // maybe this object has been destroyed!
            return;
        }
        if (typeof contextOrTimeout === 'number') {
            timeout = +contextOrTimeout;
            contextOrTimeout = null;
        }

        const timer = setTimeout(() => {
            if (!this._timerCache) {
                // maybe this object has been destroyed!
                return;
            }
            const idx = this._timerCache.indexOf(timer);
            if (idx != -1) {
                this._timerCache.splice(idx, 1);
            }
            this.safeInvokeCallback(contextOrTimeout, handler);
        }, timeout);
        this._timerCache.push(timer);
        return timer;
    }

    /**
     * 终止`callLater`所发起的异步任务，用法与`clearTimeout`非常类似，但是更加安全，
     * 可以顺便将`callLater`所缓存的定时器序号删除。
     *
     */
    protected clearCallLater(handle: number): void {
        clearTimeout(handle);

        if (!this._timerCache) {
            return;
        }
        const idx = this._timerCache.indexOf(handle);
        if (idx != -1) {
            this._timerCache.splice(idx, 1);
        }
    }

    ngOnInit() {
        this.initialized = true;
    }


    ngOnDestroy() {
        this._timerCache.forEach(t => clearTimeout(t));
        this._timerCache = null;
    }

    //导入=======================================
    /**
     * 安全的调用一个函数，并返回该函数的返回值。如果该函数执行失败，可以在控制台给出实际的堆栈以协助排查问题
     *
     * @param context 执行函数的上下文
     */
    public safeInvokeCallback(context: any, callback: Function, args?: any[]): any {
        if (this.isUndefined(callback)) {
            return;
        }
        try {
            return callback.apply(context, args);
        } catch (e) {
            console.error('invoke callback error: ' + e);
            console.error(e.stack);
        }
    }
    /**
     * 参考 `isDefined`
     *
     * @param value
     */
    public isUndefined(value): boolean {
        return !this.isDefined(value);
    }
    /**
     * 可靠的判断一个值是否有效，输入 `""` 和 `0` 均返回true，只有`null`或者`undefined`才会返回false
     *
     * @param value 待测验的值
     */
    public isDefined(value): boolean {
        return value !== undefined && value !== null;
    }
    /**
     *
     * @param element
     * @param selector 支持'.className' '#id' '[attr]' 'tagName'
     */
    public  getParentNodeBySelector(element: HTMLElement, selector: string): HTMLElement {
        if (element instanceof HTMLElement) {
            let parent = element.parentElement;
            selector = selector.trim();
            if (selector.match(/^#.+/)) {
                selector = selector.replace("#", '');
                while (parent && parent.getAttribute('id') !== selector) {
                    parent = parent.parentElement;
                }
                return parent;
            } else if (selector.match(/^\..+/)) {
                selector = selector.replace(".", '');
                while (parent && !parent.classList.contains(selector)) {
                    parent = parent.parentElement;
                }
                return parent;
            } else if (selector.match(/^\[.+\]$/)) {
                selector = selector.replace(/[\[\]]/g, '');
                while (parent && !parent.hasAttribute(selector)) {
                    parent = parent.parentElement;
                }
                return parent;
            } else {
                while (parent && parent.tagName.toLowerCase() !== selector) {
                    parent = parent.parentElement;
                }
                return parent;
            }
        } else {
            return null;
        }
    }

    
    /**
     * 把一个值转为px或%
     * @param value
     * @returns string
     */
    public getCssValue(value: string | number): string {
        if (this.isUndefined(value)) return null;
        value = typeof value === 'string' ? value : value + '';
        const match = value ? value.match(/^\s*\d+\.*\d*\s*$/) : null;
        return match ? (value + 'px') : value;
    }

}

/**
 * 一般来说，应用无需关注此类
 */
export abstract class AbstractJigsawComponent extends AbstractJigsawViewBase implements IJigsawComponent {
    /**
     * @internal
     */
    @Input()
    public basicClass: string;

    protected _width: string;
    protected _height: string;
    protected _maxHeight: string;

    @Input()
    public get width(): string {
        return this._width;
    }

    public set width(value: string) {
        this._width = this.getCssValue(value);
    }

    @Input()
    public get height(): string {
        return this._height;
    }

    public set height(value: string) {
        this._height = this.getCssValue(value);
    }

    @Input()
    public get maxHeight(): string {
        return this._maxHeight;
    }

    public set maxHeight(value: string) {
        this._maxHeight = this.getCssValue(value);
    }




}

export interface IJigsawFormControl {
    valid: boolean;
}
